import type {
  DecisionDomain,
  DecisionSupport,
  DrugSafetyReport,
  PanelAnswer,
  PanelCellKey,
  PanelOverrides,
} from "@/types/report";

// Chariot 0.9 stage 8, owner option (b): a clinician's decision replaces the
// panel cell doctors see. The chosen answers live in `panel_overrides` on the
// report version doc; the generated report is never rewritten. Applied where
// the report is read (useLibraryReport, the admin preview), so the panel
// itself renders an overridden cell exactly like any other: no badge, no text
// saying it was reviewed (docs/legal-compliance.md item 5).
//
// A report without overrides (every report before stage 8) is returned as is,
// and so is a pre-split panel, which has no per-cell decisions.

export const PANEL_CELL_KEYS: PanelCellKey[] = [
  "fertility.female",
  "fertility.male",
  "pregnancy.overall",
  "pregnancy.first_trimester",
  "pregnancy.second_trimester",
  "pregnancy.third_trimester",
  "lactation",
];

function isSplit(
  decision: DrugSafetyReport["decision_support"],
): decision is DecisionSupport {
  return Boolean(decision) && !("verdict" in decision!.fertility);
}

function withAnswer(domain: DecisionDomain, answer: PanelAnswer): DecisionDomain {
  return {
    ...domain,
    verdict: answer.verdict,
    evidence_strength: answer.evidence_strength ?? null,
    key_figure: answer.key_figure,
    key_figure_source_id: answer.key_figure_source_id ?? null,
    key_figure_baseline: answer.key_figure_baseline ?? null,
    rationale: answer.rationale,
    source_ids: answer.source_ids ?? [],
  };
}

export function applyPanelOverrides(
  report: DrugSafetyReport,
  overrides?: PanelOverrides | null,
): DrugSafetyReport {
  const decision = report.decision_support;
  if (!overrides || !isSplit(decision)) return report;
  const pick = (key: PanelCellKey, domain: DecisionDomain) => {
    const answer = overrides[key];
    return answer ? withAnswer(domain, answer) : domain;
  };
  if (!PANEL_CELL_KEYS.some((key) => overrides[key])) return report;
  return {
    ...report,
    decision_support: {
      ...decision,
      fertility: {
        female: pick("fertility.female", decision.fertility.female),
        male: pick("fertility.male", decision.fertility.male),
      },
      pregnancy: {
        overall: pick("pregnancy.overall", decision.pregnancy.overall),
        first_trimester: pick(
          "pregnancy.first_trimester",
          decision.pregnancy.first_trimester,
        ),
        second_trimester: pick(
          "pregnancy.second_trimester",
          decision.pregnancy.second_trimester,
        ),
        third_trimester: pick(
          "pregnancy.third_trimester",
          decision.pregnancy.third_trimester,
        ),
      },
      lactation: pick("lactation", decision.lactation),
    },
  };
}
